import { AsyncPipe, CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatOptionModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatToolbarModule } from '@angular/material/toolbar';
import { OwlDateTimeModule } from '@danielmoncada/angular-datetime-picker';
import moment from 'moment';
import { DictionaryType } from 'src/app/apiAndObjects/api/dictionaryType.enum';
import { BoundingBox } from 'src/app/bgsLeaflet/public_api';
import { DictionaryService } from 'src/app/services/dictionary.service';
import { ObservationsService } from 'src/app/services/observations.service';
import { Dictionary } from '../../apiAndObjects/public_api';
import { EVMapService } from '../ev-map/ev-map.service';
import { EVSidebarService } from '../ev-sidebar/ev-sidebar.service';

@Component({
  selector: 'app-ev-filter',
  templateUrl: './ev-filter.component.html',
  styleUrls: ['./ev-filter.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatToolbarModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatOptionModule,
    OwlDateTimeModule,
    AsyncPipe,
    MatCheckboxModule,
    ReactiveFormsModule,
    MatDatepickerModule,
  ],
})
export class EvFilterComponent implements OnInit {
  public form: UntypedFormGroup;
  public resetDisabled = true;
  public submitDisabled = true;
  public filterSubmission: object;
  public bboxTextArray = new Array<string>();
  public bboxDrawMode: 'EMPTY' | 'DRAWING' | 'SET' = 'EMPTY';

  observationTypesDictionary: Dictionary;
  countriesUsedDictionary: Dictionary;
  sourcesDictionary: Dictionary;
  keywordsDictionary: Dictionary;

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly sidebarService: EVSidebarService,
    private readonly observationsService: ObservationsService,
    private readonly dictionaryService: DictionaryService,
    private readonly mapService: EVMapService,
  ) {
    dictionaryService
      .getDictionary(DictionaryType.OBSERVATION_TYPES)
      .then((dict: Dictionary) => (this.observationTypesDictionary = dict));
    this.dictionaryService
      .getDictionary(DictionaryType.COUNTRIES_USED)
      .then((dict: Dictionary) => (this.countriesUsedDictionary = dict));
    dictionaryService.getDictionary(DictionaryType.SOURCES).then((dict: Dictionary) => (this.sourcesDictionary = dict));
    dictionaryService
      .getDictionary(DictionaryType.KEYWORDS)
      .then((dict: Dictionary) => (this.keywordsDictionary = dict));
  }

  ngOnInit() {
    this.initialiseSearchForm();

    this.mapService.watchBoundingBox().subscribe((bbox: BoundingBox) => {
      if (!bbox.isBounded()) {
        this.bboxTextArray = [];
      } else {
        this.bboxTextArray = [
          String(this.toPrecision(bbox.getMaxLat())).valueOf() + 'N',
          String(this.toPrecision(bbox.getMinLon())).valueOf() + 'W',
          String(this.toPrecision(bbox.getMaxLon())).valueOf() + 'E',
          String(this.toPrecision(bbox.getMinLat())).valueOf() + 'S',
        ];
      }
      this.bboxDrawMode = bbox.isBounded() ? 'SET' : 'EMPTY';
      this.form.get('minX').setValue(bbox.getMinLon());
      this.form.get('maxX').setValue(bbox.getMaxLon());
      this.form.get('minY').setValue(bbox.getMinLat());
      this.form.get('maxY').setValue(bbox.getMaxLat());
      // console.debug('form', this.form.value);
    });
  }

  get countryControl() {
    return this.form.get('country');
  }

  public closeSidePanel() {
    this.sidebarService.clearSidebarMode();
  }

  public initialiseSearchForm(): void {
    this.form = this.fb.group({
      freetext: [null, []],
      startdate: [null, []],
      enddate: [null, []],
      country: [null, []],
      source: [null, []],
      observationType: [null, []],
      keyword: [null, []],
      hasPhoto: [false, []],
      minX: [null, []],
      maxX: [null, []],
      minY: [null, []],
      maxY: [null, []],
    });
    this.form.statusChanges.subscribe(() => {
      this.submitDisabled = !this.form.valid;
    });
    this.form.valueChanges.subscribe(() => {
      this.resetDisabled = !this.form.dirty;
    });
  }

   
  get country(): any {
    return this.form.get('country');
  }
  resetCountry(): void {
    this.form.controls.country.reset();
  }

   
  get source(): any {
    return this.form.get('source');
  }
  resetSource(): void {
    this.form.controls.source.reset();
  }

   
  get observationType(): any {
    return this.form.get('observationType');
  }
  resetObservationType(): void {
    this.form.controls.observationType.reset();
  }

   
  get keyword(): any {
    return this.form.get('keyword');
  }
  resetKeyword(): void {
    this.form.controls.keyword.reset();
  }

  reset() {
    this.form.reset();
  }
  public submit(): void {
    this.form.disable();

    const submitObj = {
      ...this.form.value,
    };
    // align values with api
    submitObj.hasPhoto = submitObj.hasPhoto ? 'Y' : null;

    // Send filters with date time
    // submitObj.startdate = moment(submitObj.startdate).toISOString();
    // submitObj.enddate = moment(submitObj.enddate).toISOString();

    // Send filter with date only
    if (submitObj.startdate && submitObj.enddate) {
      submitObj.startdate = moment(submitObj.startdate).format(moment.HTML5_FMT.DATE);
      submitObj.enddate = moment(submitObj.enddate).format(moment.HTML5_FMT.DATE);
    }

    this.observationsService.search(submitObj).finally(() => {
      this.form.enable();
    });
  }

  public startDrawBoundingBox(): void {
    this.bboxDrawMode = 'DRAWING';
    this.mapService.startDrawBoundingBox();
  }

  public clearBoundingBox(): void {
    this.mapService.clearBoundingBox();
  }

  private toPrecision(value: number, precisionDP = 3): number {
    return Math.round(value * Math.pow(10, precisionDP)) / Math.pow(10, precisionDP);
  }
}
